import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CircularActivityIndicator,
  Dropdown,
  PhoneTextField,
  PrimaryButton,
  TextField,
} from '@cxc/react-fds-components'
import { useUniversalContext } from '@cxc/react-universal-components'
import ProductInfo from '../ProductInfo/ProductInfo'
import { getGeocodeAddress } from '../../providers/HereMapClient'
import { Customer } from '../../types/models'
import { calculateShippingAmount } from '../../utils/checkout'
import {
  countryRegionOptions,
  EcomUniversalContextProperty,
  EcomUniversalContextScope,
  stateProvinceMap,
} from '../../utils/constants'
import { Route } from '../../routes'
import { track } from '../../analytics/analytics'
import { userInfoDigitalData } from '../../analytics/defaultDigitalData'
import './CustomerInfoForm.css'
import '../Common.css'
import ErrorBanner from '../ErrorBanner/ErrorBanner'
import {
  ContactInfoColumn,
  CustomerInfoBreadcrumbs,
  CustomerInfoFormContainer,
  GridCell,
  ProductInfoColumn,
} from './CustomerInfoFormStyleComponents'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  CustomerInfoFormInput,
  getCustomerInfoFormSchema,
  getEmptyCustomerInfo,
} from './customerInfoValidation'
import {
  buildGeocodeRequest,
  buildShippingAddress,
} from './customerInfoFormUtils'
import { useUserInfo } from './useUserInfo'
import { logger } from '../../utils/logger'
import { ShippingAddressGeocode } from '../../types/hereMaps'
import { GeocodeValidation, validateGeocodeResult } from '../../utils/geocode'
import { useLocale } from '../../hooks/useLocale'
import { AddressFieldWithSuggestions } from './AddressFieldWithSuggestions'
import { useTranslation } from 'react-i18next'

// This is used in logs and is not displayed to the customer.
const GEOCODE_ERROR =
  'There was an error fetching or parsing geocode response: '

function CustomerInfoForm() {
  const { t } = useTranslation()
  const { getCartItems, setScopedValue, isLoggedIn } = useUniversalContext()
  const navigate = useNavigate()
  const { countryCode, currency, locale, lowerCaseLocale } = useLocale()
  const stateProvinceOptions = stateProvinceMap[countryCode]

  const [isContinueButtonLoading, setIsContinueButtonLoading] = useState(false)
  const [error, setError] = useState('')

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    setValue,
    trigger,
    watch,
  } = useForm({
    defaultValues: getEmptyCustomerInfo(t, countryCode),
    resolver: zodResolver(getCustomerInfoFormSchema(t, countryCode)),
    mode: 'onBlur',
  })

  const { loading: userInfoLoading } = useUserInfo(setValue, trigger)

  const phoneNumberWatch = watch('phoneNumber')

  useEffect(() => {
    // Revalidate form if we have supplied the phone number via user info,
    // so the user does not get stuck with a filled in phone number that
    // hasn't validated properly. Skip this step if not logged in, so a user
    // filling the form out manually doesn't have the address section
    // flag red as soon as they've filled in their phone number.
    if (isLoggedIn && phoneNumberWatch.isValid) {
      void trigger()
    }
  }, [phoneNumberWatch, trigger, isLoggedIn])

  const shippingAmount = calculateShippingAmount(getCartItems(), 'US')

  useEffect(() => {
    if (getCartItems().length === 0) {
      navigate(`/${lowerCaseLocale}${Route.ProductList}`)
    }
  }, [navigate, getCartItems, lowerCaseLocale])

  useEffect(() => {
    if (isValid) {
      setError('')
    }
  }, [isValid])

  useEffect(() => {
    track('ecom-checkout-1', userInfoDigitalData, currency, locale, {
      cartItems: getCartItems(),
      shippingAmount: shippingAmount,
    })
  }, [currency, getCartItems, locale, shippingAmount])

  function handleFetchAddressError(errorMessage: string) {
    setError(errorMessage)
  }

  function handleAPIError(
    logMessage: string,
    message: string = t('errors.formInput'),
    error: unknown = message,
  ) {
    logger('warn', logMessage, error)
    setError(message)
    setIsContinueButtonLoading(false)
  }

  const onContinueButtonClick = async (formData: CustomerInfoFormInput) => {
    setIsContinueButtonLoading(true)

    const geocodeRequest = buildGeocodeRequest(formData)

    await getGeocodeAddress(geocodeRequest)
      .then((geocodeResponse) => {
        const validationResult = validateGeocodeResult(geocodeResponse)

        if (validationResult === GeocodeValidation.NoMatch) {
          handleAPIError(GEOCODE_ERROR, t('errors.noMatchingAddress'))
          return
        } else if (validationResult === GeocodeValidation.Ambiguous) {
          handleAPIError(GEOCODE_ERROR, t('errors.addressAmbiguous'))
          return
        }

        const geocodeItemAddress = geocodeResponse.items[0].address

        const shippingAddress = buildShippingAddress(formData)
        const customer: Customer = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phoneNumber.value,
          companyName: formData.companyLegalName,
          fin: formData.finCode,
        }
        setScopedValue(
          EcomUniversalContextScope.Ecom,
          EcomUniversalContextProperty.Customer,
          JSON.stringify(customer),
        )
        setScopedValue(
          EcomUniversalContextScope.Ecom,
          EcomUniversalContextProperty.ShippingAddress,
          JSON.stringify(shippingAddress),
        )
        const shippingAddressGeocode: ShippingAddressGeocode = {
          addressLine:
            geocodeItemAddress.houseNumber + ' ' + geocodeItemAddress.street,
          postalCode: geocodeItemAddress.postalCode,
          city: geocodeItemAddress.city,
          state: geocodeItemAddress.stateCode,
          country: geocodeItemAddress.countryCode,
        }
        setScopedValue(
          EcomUniversalContextScope.Ecom,
          EcomUniversalContextProperty.ShippingAddressGeocode,
          JSON.stringify(shippingAddressGeocode),
        )

        setIsContinueButtonLoading(false)
        navigate(`/${lowerCaseLocale}${Route.PaymentCheckout}`, {
          state: 'contact_shipping',
        })
      })
      .catch((error) => {
        handleAPIError(GEOCODE_ERROR, t('errors.tryAgainLater'), error)
      })
  }

  const onValidSubmit: SubmitHandler<CustomerInfoFormInput> = (formData) => {
    void onContinueButtonClick(formData)
  }

  const onErrorSubmit = () => {
    setError(t('errors.formInput'))
    setIsContinueButtonLoading(false)
  }

  return (
    <CustomerInfoFormContainer>
      <CustomerInfoBreadcrumbs locale={lowerCaseLocale} />
      <ContactInfoColumn>
        <form
          className={'fds-layout-grid__inner grid-gutter-spacing'}
          onSubmit={handleSubmit(onValidSubmit, onErrorSubmit)}
        >
          <GridCell>
            <hr />
          </GridCell>
          <ErrorBanner message={error} />
          <GridCell
            className={'fmc-type--heading4 fmc-mt-4'}
            style={{ display: 'flex' }}
          >
            <span>{t('customerInfo.contact')}</span>
            {userInfoLoading === 'loading' && (
              <CircularActivityIndicator diameter={34} />
            )}
          </GridCell>
          <Controller
            control={control}
            name={'firstName'}
            render={({ field }) => (
              <TextField
                {...field}
                message={errors[field.name]?.message}
                error={errors[field.name]}
                placeholder={t('customerInfo.form.givenName')}
                className={
                  'fds-layout-grid__cell--span-6-xl ' +
                  'fds-layout-grid__cell--span-6-lg ' +
                  'fds-layout-grid__cell--span-6-md ' +
                  'fds-layout-grid__cell--span-4-sm ' +
                  'fds-layout-grid__cell--span-6-xs '
                }
              />
            )}
          />
          <Controller
            name={'lastName'}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                message={errors[field.name]?.message}
                error={errors[field.name]}
                placeholder={t('customerInfo.form.surname')}
                className={
                  'fds-layout-grid__cell--span-6-xl ' +
                  'fds-layout-grid__cell--span-6-lg ' +
                  'fds-layout-grid__cell--span-6-md ' +
                  'fds-layout-grid__cell--span-4-sm ' +
                  'fds-layout-grid__cell--span-6-xs '
                }
              />
            )}
          />
          <Controller
            control={control}
            name={'companyLegalName'}
            render={({ field }) => (
              <TextField
                {...field}
                message={errors[field.name]?.message}
                error={errors[field.name]}
                placeholder={t('customerInfo.form.companyLegalName')}
                className={'fds-layout-grid__cell--span-12'}
              />
            )}
          />
          <Controller
            control={control}
            name={'finCode'}
            render={({ field }) => (
              <TextField
                {...field}
                message={errors[field.name]?.message}
                error={errors[field.name]}
                placeholder={t('customerInfo.form.finPlaceholder')}
                className={'fds-layout-grid__cell--span-12'}
              />
            )}
          />
          <Controller
            control={control}
            name={'email'}
            render={({ field }) => (
              <TextField
                {...field}
                message={errors[field.name]?.message}
                error={errors[field.name]}
                placeholder={t('customerInfo.form.email')}
                className={'fds-layout-grid__cell--span-12'}
              />
            )}
          />

          <Controller
            control={control}
            name={'phoneNumber'}
            render={({ field }) => (
              <div className={'fds-layout-grid__cell--span-12'}>
                {/* The value input for PhoneTextField is only used to set the
                initial state of the component, and cannot be programmatically
                changed afterward. Thus, in order to autofill the component
                and have it format and validate correctly from user information,
                we have to wait to render the component at all until we either
                have a value, or have finished checking the user info for
                one and don't have it. */}
                {(field.value.value !== '' ||
                  userInfoLoading !== 'loading') && (
                  <PhoneTextField
                    value={field.value.value}
                    onValueChange={(value) => field.onChange(value)}
                    placeholder={t('customerInfo.form.phoneNumber')}
                    region="us"
                    message={
                      errors.phoneNumber?.value?.message ||
                      errors.phoneNumber?.formattedValue?.message ||
                      errors.phoneNumber?.isValid?.message
                    }
                    error={
                      errors.phoneNumber?.value ||
                      errors.phoneNumber?.formattedValue ||
                      errors.phoneNumber?.isValid
                    }
                  />
                )}
              </div>
            )}
          />
          <GridCell className={'fmc-type--heading4 fmc-mt-4'}>
            {t('customerInfo.shipping')}
          </GridCell>
          <GridCell className={'text-field-address'}>
            <div className={'fds-layout-grid__cell--span-12'}>
              <Controller
                control={control}
                name={'streetAddress1'}
                render={({ field }) => (
                  <AddressFieldWithSuggestions
                    field={field}
                    errors={errors}
                    setValue={setValue}
                    handleFetchAddressError={handleFetchAddressError}
                    placeholder={t('customerInfo.form.address')}
                  />
                )}
              />
            </div>
          </GridCell>
          <Controller
            control={control}
            name={'streetAddress2'}
            render={({ field }) => (
              <TextField
                {...field}
                message={errors[field.name]?.message}
                error={errors[field.name]}
                placeholder={t('customerInfo.form.addressLine2')}
                className={'fds-layout-grid__cell--span-12'}
              />
            )}
          />
          <Controller
            control={control}
            name={'city'}
            render={({ field }) => (
              <TextField
                {...field}
                message={errors[field.name]?.message}
                error={errors[field.name]}
                placeholder={t('customerInfo.form.city')}
                className={'fds-layout-grid__cell--span-12'}
              />
            )}
          />
          <Controller
            control={control}
            name={'country'}
            render={({ field }) => (
              <Dropdown
                className={'fds-layout-grid__cell--span-4'}
                label={t('customerInfo.form.country')}
                placeholder={field.value.label}
                options={countryRegionOptions}
                value={field.value.value}
                required
                disabled={true}
                onChange={field.onChange}
                error={
                  errors[field.name]?.label?.message ||
                  errors[field.name]?.value?.message
                }
              />
            )}
          />
          <Controller
            control={control}
            name={'stateProvince'}
            render={({ field }) => (
              <Dropdown
                className={'fds-layout-grid__cell--span-4'}
                label={t('customerInfo.form.stateProvince')}
                placeholder={field.value.label}
                options={stateProvinceOptions}
                value={field.value.value}
                required
                onChange={field.onChange}
                error={
                  errors[field.name]?.label?.message ||
                  errors[field.name]?.value?.message
                }
              />
            )}
          />
          <Controller
            control={control}
            name={'postalCode'}
            render={({ field }) => (
              <TextField
                {...field}
                message={errors[field.name]?.message}
                error={errors[field.name]}
                placeholder={t('customerInfo.form.postalCode')}
                className={'fds-layout-grid__cell--span-4'}
              />
            )}
          />
          <GridCell className={'fmc-my-4 fds-align--center'}>
            <PrimaryButton
              title={t('customerInfo.continue')}
              iconName={'chevron-right'}
              type={'submit'}
              loading={isContinueButtonLoading}
              disabled={!isDirty}
            />
          </GridCell>
          <GridCell>
            <hr />
          </GridCell>
          <GridCell>
            <div className={'fmc-type--body2 fmc-my-3 fmc-pb-9'}>
              {t('common.copyrightNotice')}
            </div>
          </GridCell>
        </form>
      </ContactInfoColumn>
      <ProductInfoColumn>
        <ProductInfo />
      </ProductInfoColumn>
    </CustomerInfoFormContainer>
  )
}

export default CustomerInfoForm
